import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import {
    matchesNavigation,
    type NavigationItem,
} from "@/components/workspace/navigation";

interface NavigationLinksProps {
    items: NavigationItem[];
    favorites: string[];
    collapsed?: boolean;
    onFavorite: (id: string) => void;
    onNavigate?: () => void;
}

export const NavigationLinks = ({
    items,
    favorites,
    collapsed,
    onFavorite,
    onNavigate,
}: NavigationLinksProps) => {
    const { pathname } = useLocation();
    const { t } = useTranslation("global");
    const [flyingLabel, setFlyingLabel] = useState<{
        text: string;
        icon: NavigationItem["icon"];
        x: number;
        y: number;
        targetX: number;
        targetY: number;
    } | null>(null);

    const toggleFavorite = (
        event: React.MouseEvent<HTMLButtonElement>,
        item: NavigationItem,
        isFavorite: boolean
    ): void => {
        if (!isFavorite) {
            const source = event.currentTarget.getBoundingClientRect();
            const target = document
                .querySelector<HTMLElement>('[data-favorites-trigger="true"]')
                ?.getBoundingClientRect();

            if (target) {
                setFlyingLabel({
                    text: item.label,
                    icon: item.icon,
                    x: source.left,
                    y: source.top,
                    targetX: target.left - source.left,
                    targetY: target.top - source.top,
                });

                window.setTimeout(() => setFlyingLabel(null), 700);
            }
        }

        onFavorite(item.id);
        if (!isFavorite) {
            window.dispatchEvent(new CustomEvent("favorite-added"));
        }
    };
    return (
        <>
            <div className="space-y-1">
                {items.map((item) => {
                    const active = matchesNavigation(item, pathname);
                    const favorite = favorites.includes(item.id);
                    return (
                        <div
                            key={item.id}
                            className={`group flex items-center rounded-lg ${active ? "bg-primary/10 text-primary dark:bg-white/10" : "text-slate-600 hover:bg-slate-100 dark:text-zinc-300 dark:hover:bg-white/5"}`}
                        >
                            <Link
                                to={item.path || "/"}
                                onClick={onNavigate}
                                title={collapsed ? item.label : undefined}
                                aria-current={active ? "page" : undefined}
                                className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg py-2.5 text-sm font-medium !text-inherit outline-offset-2 focus-visible:outline-2 focus-visible:outline-primary ${collapsed ? "justify-center px-2" : "pl-3 pr-1"}`}
                            >
                                <Icon
                                    value={item.icon}
                                    size={18}
                                    className="shrink-0"
                                />
                                {!collapsed && (
                                    <span className="truncate">
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                            {!collapsed &&
                                !["home", "account"].includes(item.id) && (
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        onlyicon
                                        icon={
                                            <Icon
                                                value="heart"
                                                fill={
                                                    favorite
                                                        ? "currentColor"
                                                        : "none"
                                                }
                                            />
                                        }
                                        onClick={(event) =>
                                            toggleFavorite(
                                                event,
                                                item,
                                                favorite
                                            )
                                        }
                                        aria-pressed={favorite}
                                        aria-label={
                                            t(
                                                favorite
                                                    ? "menu.favorites.tooltip_remove"
                                                    : "menu.favorites.tooltip_add"
                                            ) +
                                            ": " +
                                            item.label
                                        }
                                        title={t(
                                            favorite
                                                ? "menu.favorites.tooltip_remove"
                                                : "menu.favorites.tooltip_add"
                                        )}
                                        className={`mr-1 !h-8 !w-8 shrink-0 !p-0 ${favorite ? "!text-primary dark:!text-primary-300" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"}`}
                                    />
                                )}
                        </div>
                    );
                })}
            </div>
            {flyingLabel &&
                createPortal(
                    <div
                        className="favorite-flying-label"
                        style={
                            {
                                left: flyingLabel.x,
                                top: flyingLabel.y,
                                "--favorite-target-x": `${flyingLabel.targetX}px`,
                                "--favorite-target-y": `${flyingLabel.targetY}px`,
                            } as React.CSSProperties
                        }
                    >
                        <Icon value={flyingLabel.icon} size={18} />
                        {flyingLabel.text}
                    </div>,
                    document.body
                )}
        </>
    );
};
